import { useNavigation, useRoute } from '@react-navigation/native';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Pressable, SafeAreaView, View } from 'react-native';
import { Text } from '@/components/Text';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useStoreActions, useStoreState } from '@/stores/store';

import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import SquareGrid from '@/components/Util/reactSquareGrid';
import encryptedStorage from '../../stores/encryptedStorage';
import moment from 'moment';
import api from '../../api/axios';
// styles
import Entypo from '@expo/vector-icons/Entypo';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import colors from '@/config/colors';
import { ExerciseCard } from '../Aufgaben/ExerciseCard';
import { SelectLayout } from '@/components/SelectLayout';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { ProgressCharts } from '@/components/CustomWebView/ProgressCharts';

const _STORAGE = encryptedStorage();

const uploadApi = async (id) => {
  const body = {
    practiceSessions: (await _STORAGE.getItem(`sessionBuffer${id}`)) ?? [],
  };
  const { data } = await api.post(
    `/api/patients/${id}/practicesessions`,
    body,
    {
      timeout: 25000,
    },
  );
  return data;
};

const getStarContainer = (userId) => {
  let starContainerKey = 'starContainer' + userId;
  return new Promise(async (resolve, reject) => {
    try {
      let container = await _STORAGE.getItem(starContainerKey);
      if (container === null) {
        const defaultValue = {
          buffer: [],
          store: {},
          lastProgress: {
            date: moment().format('YYYY-MM-DD'),
            stars: 0,
          },
        };
        container = defaultValue;
        await _STORAGE.setItem(starContainerKey, defaultValue);
      }
      resolve(container);
    } catch (error) {
      reject(error);
    }
  });
};

// Initialize this function only once during mounting
const determineSetType = (setType) => {
  if (setType === 'textset') {
    return 'Texte';
  } else if (setType === 'wordset') {
    return 'Wörter';
  } else if (setType === 'sentenceset') {
    return 'Sätze';
  }
};

export function SelfTraining() {
  const { params } = useRoute();
  const { activeSets = [], selectedPatient, preview } = params;
  const [sets, setSets] = useState([]);
  const [activeSet, setActiveSet] = useState(activeSets[0]);
  const [showModal, setShowModal] = useState(false);
  const { eigentrainingLock, appLockPattern } = useStoreState((state) => state);
  const { isTherapistDemo, downloadPatients } = useStoreState((store) => ({
    isTherapistDemo: store.isTherapistDemo,
    downloadPatients: store.downloadPatients,
  }));
  const resetSelfTrainingSelection = useStoreActions(
    (actions) => actions.resetSelfTrainingSelection,
  );
  const { navigate, goBack, setOptions, addListener, dispatch } =
    useNavigation();
  const iconSize = useResponsiveFontSize(9);
  const textSize = useResponsiveFontSize(4);
  const { showNotification } = useNotification();

  const isBackLocked = eigentrainingLock && appLockPattern && !preview;

  const onBack = useCallback(() => {
    resetSelfTrainingSelection();
    if (isBackLocked) {
      navigate('CheckPassword', { blockBackFromSelfTraining: true });
    } else {
      goBack();
    }
  }, []);

  const generateActiveSets = useCallback(async () => {
    const activeSetsMapped = activeSets.map((key) => _STORAGE.getItem(key));
    return await Promise.all(activeSetsMapped);
  }, []);

  const navigateToSelectExercise = useCallback(() => {
    const index = activeSets.findIndex((set) => set === activeSet);
    const set = sets[index];
    const { exerciseClass, patientId } = set;
    const exerciseClassName = `${exerciseClass}set`;

    navigate('Aufgaben', {
      exerciseClass: exerciseClassName,
      patientId,
      setId: activeSet,
      exerciseSettings: set,
      isSelf: true,
      selectedPatient,
      activeSets,
      preview,
    });
  }, [activeSet, sets, preview]);

  const openStatistics = useCallback(async () => {
    try {
      const id = selectedPatient?._id;
      const container = await getStarContainer(id);

      if (isTherapistDemo) {
        setShowModal(true);
      } else {
        const { practiceDurations = [] } = await uploadApi(id);
        const key = `starContainer${id}`;
        const storedSessions = {};

        practiceDurations.forEach(function (duration) {
          duration._id.month = duration._id.month - 1;
          storedSessions[moment(duration._id).format('YYYY-MM-DD')] = {
            duration: duration.duration,
          };
        });
        if (container?.store) {
          container.store = storedSessions;
        }
        await _STORAGE.setItem(key, container);
        setShowModal(true);
      }
    } catch (error) {
      logError('Could not open statistics', error);
    }
  }, [selectedPatient?._id]);

  const leftSideBtns = () => {
    return sets.map((set) => {
      try {
        const patientData = downloadPatients.find(
          (patient) => patient._id === set.patientId,
        );
        const { exerciseClass, activeWordset } = set;
        const allSets = patientData[exerciseClass + 'sets'];
        const keyName = exerciseClass + 'set';
        const data = activeWordset[keyName];
        const SET_DATA = allSets.find((sets) => sets._id === data._id);
        const itemsKey = keyName + 'Items';
        const isActive = activeSet === data._id;
        const label = SET_DATA.appDisplayName
          ? SET_DATA.appDisplayName
          : determineSetType(keyName);

        const onPress = () => {
          setActiveSet(data._id);
        };

        const imageSource =
          keyName === 'textset'
            ? require('../../assets/text_icon.png')
            : data[itemsKey][0].word
              ? { uri: data[itemsKey][0].word.pictures[0] }
              : { uri: data[itemsKey][0].sentence.pictures[0] };

        return (
          <ExerciseCard
            key={label}
            label={label}
            isActive={isActive}
            onPress={onPress}
            imageSrc={imageSource}
          />
        );
      } catch {
        showNotification({
          type: 'error',
          title: 'Unerwarteter Fehler',
        });
      }
    });
  };

  const rightSideBtns = useMemo(
    () =>
      [
        {
          id: 1,
          iconName: 'star',
          color: colors.yellow,
          text: null,
          onPress: openStatistics,
        },
        {
          id: 2,
          iconName: 'arrow-bold-right',
          color: colors.primary,
          text: 'weiter',
          textColor: colors.primary,
          onPress: navigateToSelectExercise,
        },
      ].map((btn) => {
        const { id, iconName, color, text, textColor, onPress } = btn;
        const onlyIcon = text === null;
        const textStyles = {
          color: textColor,
          fontSize: textSize,
          fontWeight: '700',
        };
        const wrapperStyles = {
          justifyContent: 'center',
          alignItems: 'center',
        };
        return (
          <PressableNeolexon key={id} onPress={onPress}>
            {onlyIcon ? (
              <Entypo name={iconName} color={color} size={iconSize} />
            ) : (
              <View style={wrapperStyles}>
                <Entypo name={iconName} color={color} size={iconSize} />
                <Text style={textStyles}>{text}</Text>
              </View>
            )}
          </PressableNeolexon>
        );
      }),
    [navigateToSelectExercise],
  );

  useEffect(() => {
    // IEF
    (async () => {
      try {
        const generatedSets = await generateActiveSets();
        const patientId =
          generatedSets && generatedSets.length
            ? generatedSets[0]?.patientId
            : null;
        if (generatedSets?.length !== activeSets?.length) {
          throw Error(`patiendId: ${patientId}`);
        }
        setSets(generatedSets);
      } catch (error) {
        logError(`Could not retrieve sets ${activeSets} for patient`, error);
        showNotification({
          type: 'error',
          title:
            'Es gab ein Problem beim Starten der Übung. Bitte laden Sie die Übungssets im Patienten-Profil erneut herunter.',
        });
        onBack();
      }
    })();

    const unsubscribe = addListener('beforeRemove', (e) => {
      e.preventDefault();
      if (!isBackLocked) {
        return dispatch(e.data.action);
      }
    });

    return unsubscribe;
  }, []);

  useLayoutEffect(() => {
    setOptions({
      headerLeft: () => (
        <Pressable onPress={onBack}>
          <Icon name="home" size={36} color="#ffff" />
        </Pressable>
      ),
    });
  }, [onBack]);

  return (
    <SafeAreaView className="flex-1 bg-black">
      <ConfirmModal
        isVisible={showModal}
        confirmText={'weiter'}
        confirmAction={() => setShowModal(false)}
        hasWebView
      >
        <ProgressCharts patientId={selectedPatient?._id} />
      </ConfirmModal>
      <View className="flex-1 bg-blue-neolexonlight">
        <SelectLayout
          leftGrid={<SquareGrid rows={2} columns={2} items={leftSideBtns()} />}
          rightGrid={rightSideBtns}
        />
      </View>
    </SafeAreaView>
  );
}
